import { logger } from '@/ui'
import DigitalSambaEmbedded from '@digitalsamba/embedded-sdk'
import { XIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface DigitalSambaRoomProps {
	joinUrl: string
	onLeave: () => void
	/** Popout mode: hide this component's header and Digital Samba's own bars, leaving just the video. */
	minimal?: boolean
}

type ConnectionState = 'connecting' | 'connected' | 'error'

const STATUS_DOT_COLOR: Record<ConnectionState, string> = {
	connecting: '#f59e0b',
	connected: '#33cc66',
	error: '#ef4444',
}

/**
 * The recruiter's Jobilla Meet call, embedded in place (fills its container rather than
 * taking over the viewport) so it can live inside the candidate card's Meeting tab with the
 * candidate's overview beside it. `onLeave` fires when the recruiter leaves or the session
 * ends, letting the host tab drop back to a rejoin prompt.
 */
export function DigitalSambaRoom({ joinUrl, onLeave, minimal = false }: DigitalSambaRoomProps) {
	const { t } = useTranslation('common')
	const containerRef = useRef<HTMLDivElement>(null)
	const sambaRef = useRef<DigitalSambaEmbedded | null>(null)
	const [state, setState] = useState<ConnectionState>('connecting')

	// Keep the latest onLeave in a ref so the mount effect below doesn't depend on its
	// identity. The parent (the Jobilla Meet tab) re-renders every second while a meeting is
	// live and passes a fresh onLeave each render; without this, the effect would tear the
	// iframe down and rebuild it every render — the call flickered and the DS SDK
	// re-initialised over and over.
	const onLeaveRef = useRef(onLeave)
	useEffect(() => {
		onLeaveRef.current = onLeave
	}, [onLeave])

	const handleLeave = useCallback(() => {
		sambaRef.current?.leaveSession()
		sambaRef.current = null
		onLeaveRef.current()
	}, [])

	useEffect(() => {
		const container = containerRef.current
		if (!container) {
			return
		}

		const sambaFrame = DigitalSambaEmbedded.createControl({
			url: joinUrl,
			root: container,
		})
		sambaRef.current = sambaFrame

		sambaFrame.on('frameLoaded', () => {
			setState('connecting')
			sambaFrame.changeBrandingOptions({
				paletteMode: 'dark',
				primaryColor: '#9a4bf5',
				toolbarColor: '#22185f',
				roomBackgroundColor: '#1a1a2e',
			})
		})
		sambaFrame.on('userJoined', () => setState('connected'))
		sambaFrame.on('sessionEnded', () => handleLeave())
		// Only a genuine media/connection failure should block the call with the error overlay.
		sambaFrame.on('mediaConnectionFailed', (error: unknown) => {
			logger.error(new Error('Digital Samba media connection failed'), {
				tags: { component: 'DigitalSambaRoom' },
				data: { error },
			})
			setState('error')
		})
		// appError is Digital Samba's generic in-app error channel — often benign or recoverable
		// (e.g. a UI toggle it couldn't apply). Log it for diagnosis, but don't tear the call down.
		sambaFrame.on('appError', (error: unknown) => {
			logger.error(new Error('Digital Samba app error'), {
				tags: { component: 'DigitalSambaRoom' },
				data: { error },
			})
		})

		sambaFrame.load()

		return () => {
			sambaRef.current?.leaveSession()
			container.replaceChildren()
			sambaRef.current = null
		}
	}, [joinUrl, handleLeave])

	// Popout mode shows just the video — hide Digital Samba's own bars (its in-call controls
	// live there in full mode). Applied once connected, and again whenever the mode flips.
	useEffect(() => {
		const frame = sambaRef.current
		if (frame === null || state !== 'connected') {
			return
		}
		if (minimal) {
			frame.hideToolbar()
			frame.hideTopbar()
		} else {
			frame.showToolbar()
			frame.showTopbar()
		}
	}, [minimal, state])

	return (
		<div className="relative flex h-full flex-col bg-brand-2000">
			{minimal ? null : (
				<div className="flex items-center justify-between border-b border-brand-900/30 px-4 py-2">
					<div className="flex items-center gap-2">
						<div className="size-2 rounded-full" style={{ backgroundColor: STATUS_DOT_COLOR[state] }} />
						<span className="text-sm font-medium text-white/80">
							{state === 'connecting' && t('interviews.videoCall.connecting')}
							{state === 'connected' && t('interviews.videoCall.connected')}
							{state === 'error' && t('interviews.videoCall.error')}
						</span>
					</div>
					<button
						type="button"
						onClick={handleLeave}
						className="flex cursor-pointer items-center gap-1.5 rounded p-1.5 text-sm text-white/60 transition-colors hover:bg-brand-900/30 hover:text-white"
					>
						<XIcon className="size-4" />
						{t('interviews.videoCall.leave')}
					</button>
				</div>
			)}

			<div ref={containerRef} className="min-h-0 flex-1 [&>iframe]:size-full" />

			{state === 'error' && (
				<div className="absolute inset-0 flex items-center justify-center bg-brand-2000/90">
					<div className="max-w-sm rounded-xl bg-white p-8 text-center shadow-2xl">
						<p className="mb-5 text-sm text-grey-700">{t('interviews.videoCall.errorMessage')}</p>
						<button
							type="button"
							onClick={handleLeave}
							className="cursor-pointer rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-700"
						>
							{t('interviews.videoCall.leave')}
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
